/**
 * Reactive (derived) operational problems from workspace tasks + completion projection.
 * Не создаёт сущностей на бэкенде — только клиентский слой для дашборда и контекста.
 */
import dayjs from 'dayjs';
import type { WorkspaceApplication, WorkspaceRoute, WorkspaceTask } from '../store/api/workspaceApi';
import {
  isOpenWorkspaceTask,
  requiresClosureProjection,
  workspaceTaskIsOverdue,
} from './dashboardTaskKpiSelectors';
import { getWorkspaceTaskSemantics, isFinanceWorkspaceTask } from './workspaceTaskSemantics';

export type WorkflowReactiveProblemType =
  | 'finance_overdue'
  | 'document_blocker'
  | 'closure_blocker'
  | 'operational_overdue'
  | 'approaching_due';

export type WorkflowReactiveSeverity = 'warning' | 'attention' | 'critical';

export type ReactiveWorkflowProblem = {
  key: string;
  problemType: WorkflowReactiveProblemType;
  severity: WorkflowReactiveSeverity;
  title: string;
  hint?: string;
  task: WorkspaceTask | null;
  workspaceLink?: boolean;
  context?: { kind: 'route' | 'application'; id: number };
};

const SLA_CRITICAL_OVERDUE_DAYS = 7;

function normalizeRole(role: string | null | undefined): string {
  return String(role || '').trim().toLowerCase();
}

function isFinanceRole(role: string): boolean {
  return role === 'accountant' || role === 'finance';
}

function isManagementRole(role: string): boolean {
  return role === 'admin' || role === 'manager' || role === 'director';
}

function isLogistLike(role: string): boolean {
  return role === 'logist' || role === 'dispatcher';
}

function overdueSeverityForTask(task: WorkspaceTask): WorkflowReactiveSeverity {
  if (!task.dueDate) return 'attention';
  const d = dayjs(task.dueDate).startOf('day');
  if (!d.isValid()) return 'attention';
  const daysPast = dayjs().startOf('day').diff(d, 'day');
  if (daysPast >= SLA_CRITICAL_OVERDUE_DAYS) return 'critical';
  return 'attention';
}

function classifyOverdueTask(task: WorkspaceTask): WorkflowReactiveProblemType {
  if (isFinanceWorkspaceTask(task)) return 'finance_overdue';
  const sem = getWorkspaceTaskSemantics(task);
  if (sem.documentRelevant && !sem.financeRelevant) return 'document_blocker';
  if (sem.primary === 'DOCUMENT') return 'document_blocker';
  return 'operational_overdue';
}


function problemTypeVisibleForRole(pt: WorkflowReactiveProblemType, roleNorm: string): boolean {
  if (isManagementRole(roleNorm)) return true;
  if (isFinanceRole(roleNorm)) {
    return pt !== 'operational_overdue';
  }
  if (isLogistLike(roleNorm)) {
    return pt !== 'finance_overdue';
  }
  return true;
}


function routeLabel(route: { id: number; number?: string | null }): string {
  return route.number?.trim() ? String(route.number) : `№${route.id}`;
}

function applicationTitleNum(app: WorkspaceApplication): string {
  return app.internalNumber?.trim() || app.number?.trim() || String(app.id);
}

function taskOverdue(task: WorkspaceTask): boolean {
  return workspaceTaskIsOverdue(task);
}

export function deriveReactiveWorkflowProblems(params: {
  allTasks: WorkspaceTask[];
  routes: WorkspaceRoute[];
  applications: WorkspaceApplication[];
  roleName: string | null | undefined;
}): ReactiveWorkflowProblem[] {
  const roleNorm = normalizeRole(params.roleName);
  const routeById = new Map(params.routes.map((r) => [r.id, r]));
  const appById = new Map(params.applications.map((a) => [a.id, a]));

  const entityKnown = (t: WorkspaceTask): boolean => {
    if (t.routeId != null && routeById.has(t.routeId)) return true;
    if (t.applicationId != null && appById.has(t.applicationId)) return true;
    return false;
  };

  const items: ReactiveWorkflowProblem[] = [];

  const pushClosureRoute = (routeId: number) => {
    const route = routeById.get(routeId);
    if (!route) return;
    if (!problemTypeVisibleForRole('closure_blocker', roleNorm)) return;
    const projection = (route as { completionProjection?: unknown }).completionProjection;
    if (!requiresClosureProjection(projection)) return;
    const t =
      params.allTasks
        .filter((x) => x.routeId === routeId && isOpenWorkspaceTask(x))
        .sort((a, b) => (taskOverdue(b) ? 1 : 0) - (taskOverdue(a) ? 1 : 0))[0] ?? null;
    const sev: WorkflowReactiveSeverity =
      projection && (projection as { financialComplete?: boolean }).financialComplete === false ? 'critical' : 'attention';
    items.push({
      key: `rw-closure-route-${routeId}`,
      problemType: 'closure_blocker',
      severity: sev,
      title: `Маршрут ${routeLabel(route)}: рейс завершён, закрытие не завершено`,
      hint: 'Финансы / документы / задачи',
      task: t,
      workspaceLink: !t,
      context: { kind: 'route', id: routeId },
    });
  };

  const pushClosureApp = (applicationId: number) => {
    const app = appById.get(applicationId);
    if (!app) return;
    if (!problemTypeVisibleForRole('closure_blocker', roleNorm)) return;
    const projection = (app as { completionProjection?: unknown }).completionProjection;
    if (!requiresClosureProjection(projection)) return;
    const t =
      params.allTasks
        .filter((x) => x.applicationId === applicationId && isOpenWorkspaceTask(x))
        .sort((a, b) => (taskOverdue(b) ? 1 : 0) - (taskOverdue(a) ? 1 : 0))[0] ?? null;
    items.push({
      key: `rw-closure-app-${applicationId}`,
      problemType: 'closure_blocker',
      severity: 'attention',
      title: `Заявка №${applicationTitleNum(app)}: есть незакрытые этапы после перевозки`,
      task: t,
      workspaceLink: !t,
      context: { kind: 'application', id: applicationId },
    });
  };

  params.routes.forEach((r) => pushClosureRoute(r.id));
  params.applications.forEach((a) => pushClosureApp(a.id));

  type Agg = { types: Set<WorkflowReactiveProblemType>; tasks: WorkspaceTask[]; severity: WorkflowReactiveSeverity };

  const routeAgg = new Map<number, Agg>();
  const appAgg = new Map<number, Agg>();

  const ensureAgg = (map: Map<number, Agg>, id: number): Agg => {
    let v = map.get(id);
    if (!v) {
      v = { types: new Set(), tasks: [], severity: 'attention' };
      map.set(id, v);
    }
    return v;
  };

  const mergeAgg = (map: Map<number, Agg>, entityId: number, task: WorkspaceTask, pt: WorkflowReactiveProblemType) => {
    if (!problemTypeVisibleForRole(pt, roleNorm)) return;
    const a = ensureAgg(map, entityId);
    a.types.add(pt);
    if (!a.tasks.some((x) => x.id === task.id)) a.tasks.push(task);
    const s = overdueSeverityForTask(task);
    const rank = (x: WorkflowReactiveSeverity) => (x === 'critical' ? 0 : x === 'attention' ? 1 : 2);
    if (rank(s) < rank(a.severity)) a.severity = s;
  };

  for (const task of params.allTasks) {
    if (!entityKnown(task) || !taskOverdue(task)) continue;
    const pt = classifyOverdueTask(task);
    if (task.routeId != null && routeById.has(task.routeId)) {
      mergeAgg(routeAgg, task.routeId, task, pt);
    } else if (task.applicationId != null && appById.has(task.applicationId)) {
      mergeAgg(appAgg, task.applicationId, task, pt);
    }
  }

  const titleForOverdueRoute = (routeId: number, types: Set<WorkflowReactiveProblemType>): string => {
    const route = routeById.get(routeId)!;
    if (types.has('finance_overdue')) return `Просрочена финансовая задача по маршруту ${routeLabel(route)}`;
    if (types.has('document_blocker')) return `Просрочены документы по маршруту ${routeLabel(route)}`;
    return `Просрочены операционные задачи по маршруту ${routeLabel(route)}`;
  };

  const titleForOverdueApp = (applicationId: number, types: Set<WorkflowReactiveProblemType>): string => {
    const app = appById.get(applicationId)!;
    const num = applicationTitleNum(app);
    if (types.has('finance_overdue')) return `Просрочен платеж / фин. задача по заявке №${num}`;
    if (types.has('document_blocker')) return `Просрочены документы по заявке №${num}`;
    return `Просрочены задачи по заявке №${num}`;
  };

  const dominantOverdueType = (types: Set<WorkflowReactiveProblemType>): WorkflowReactiveProblemType => {
    if (types.has('finance_overdue')) return 'finance_overdue';
    if (types.has('document_blocker')) return 'document_blocker';
    return 'operational_overdue';
  };

  routeAgg.forEach((agg, routeId) => {
    if (agg.tasks.length === 0) return;
    const sorted = [...agg.tasks].sort(
      (a, b) => (a.dueDate ? dayjs(a.dueDate).valueOf() : 0) - (b.dueDate ? dayjs(b.dueDate).valueOf() : 0)
    );
    const t = sorted[0];
    items.push({
      key: `rw-overdue-route-${routeId}`,
      problemType: dominantOverdueType(agg.types),
      severity: agg.severity,
      title: titleForOverdueRoute(routeId, agg.types),
      task: t,
      context: { kind: 'route', id: routeId },
    });
  });

  appAgg.forEach((agg, applicationId) => {
    if (agg.tasks.length === 0) return;
    const sorted = [...agg.tasks].sort(
      (a, b) => (a.dueDate ? dayjs(a.dueDate).valueOf() : 0) - (b.dueDate ? dayjs(b.dueDate).valueOf() : 0)
    );
    const t = sorted[0];
    items.push({
      key: `rw-overdue-app-${applicationId}`,
      problemType: dominantOverdueType(agg.types),
      severity: agg.severity,
      title: titleForOverdueApp(applicationId, agg.types),
      task: t,
      context: { kind: 'application', id: applicationId },
    });
  });

  const uniq = new Map<string, ReactiveWorkflowProblem>();
  items.forEach((it) => {
    if (!uniq.has(it.key)) uniq.set(it.key, it);
  });
  return Array.from(uniq.values()).sort((a, b) => {
    const rank = (s: WorkflowReactiveSeverity) => (s === 'critical' ? 0 : s === 'attention' ? 1 : 2);
    if (rank(a.severity) !== rank(b.severity)) return rank(a.severity) - rank(b.severity);
    return a.key.localeCompare(b.key);
  });
}

export function countDerivedFinancePressureSignals(params: {
  derived: ReactiveWorkflowProblem[];
  reconciliationAnomalies: number;
}): number {
  const base = params.derived.filter(
    (p) => p.problemType === 'finance_overdue' || p.problemType === 'closure_blocker'
  ).length;
  return base + (params.reconciliationAnomalies > 0 ? 1 : 0);
}

export function logPhase5A1ReactiveTrace(payload: Record<string, unknown>): void {
  if (process.env.NODE_ENV !== 'development') return;
  console.debug('[PHASE_5A1][reactive-workflow]', payload);
}
